/**
 * Life Is Golden — Family lineup builder ("outfit the whole family").
 *
 * PERSON-FIRST model: the unit of interaction is a family member SLOT
 * (Dad, Mom, Kid, Toddler, Baby, + add another). Tapping an empty slot asks
 * "what's <name> wearing?" (a garment mini-sheet scoped to that person type),
 * then the proven detail DRAWER (gallery, swatches, size buttons, fit scale,
 * size-guide modal) picks color + size, and the slot visually fills with the
 * chosen mockup. A print switch re-dresses the whole lineup: garments + sizes
 * carry over, color misses become a "needs color" state (never silently
 * changed, never dropped).
 *
 * Nothing docks until the first piece is added; then a slim sticky summary
 * bar carries "N dressed · $X" + one CTA. The set posts in ONE
 * /cart/add.js call, one item per slot with a _member line-item property,
 * then CartAddEvent + an explicit cart-drawer-component.open() (Horizon's
 * drawer morphs but does not open itself).
 *
 * Savings preview (strikethrough, save line, tier nudges) renders ONLY when
 * data-discounts-enabled="true" (mirrors the real automatic discounts, which
 * are flipped together with that flag via /admin/campaign).
 *
 * The in-progress roster persists to localStorage so a paid-social bounce
 * and return does not lose the family.
 */

import { CartAddEvent, CartErrorEvent } from '@theme/events';

/* ---------------------------------------------------------------------------
   Size charts + fit reputation (per blank), garment labels, slot taxonomy.
   --------------------------------------------------------------------------- */
const LIG_BB_SIZE = {
  adult: {
    label: 'Adult unisex tee',
    blank: 'Bella+Canvas 3001',
    fit: { tag: 'Fitted', note: 'Modern retail cut. Runs a touch trim; prefer a relaxed look? Size up.', pos: 38 },
    rows: [['S', '34–37', '28'], ['M', '38–41', '29'], ['L', '42–45', '30'], ['XL', '46–49', '31'], ['2XL', '50–53', '32'], ['3XL', '54–57', '33']],
  },
  adult_crew: {
    label: 'Adult crewneck',
    blank: 'Cotton Heritage M2480',
    fit: { tag: 'Runs small', note: 'Premium fleece with a trimmer cut — most people size up one.', pos: 20 },
    rows: [['S', '33–36', '27'], ['M', '37–40', '28'], ['L', '41–43', '29'], ['XL', '44–46', '30'], ['2XL', '47–49', '31'], ['3XL', '50–52', '32']],
  },
  adult_hoodie: {
    label: 'Adult hoodie',
    blank: 'Cotton Heritage M2580',
    fit: { tag: 'Runs small', note: 'This hoodie runs small — most people size up one.', pos: 24 },
    rows: [['S', '38–41', '27'], ['M', '42–45', '28'], ['L', '46–49', '29'], ['XL', '50–53', '30'], ['2XL', '54–57', '31'], ['3XL', '58–62', '32']],
  },
  kids: {
    label: 'Youth tee',
    blank: 'Bella+Canvas 3001Y',
    fit: { tag: 'True to size', note: 'True to standard US youth sizing.', pos: 50 },
    rows: [['S (6–8)', '28–32', '20.9'], ['M (10–12)', '32–36', '22.1'], ['L (14–16)', '36–38', '23.4'], ['XL (18–20)', '38–40', '24.4']],
  },
  kids_fleece: {
    label: 'Youth crewneck',
    blank: 'Gildan 18000B',
    fit: { tag: 'True to size', note: 'True to standard US youth sizing, with a roomier fleece cut.', pos: 52 },
    rows: [['S', '34', '21.25'], ['M', '36', '22.5'], ['L', '38', '24'], ['XL', '40', '25.5']],
  },
  kids_crew_little: {
    label: 'Little kids crew',
    blank: 'Rabbit Skins 3317',
    fit: { tag: 'True to size', note: 'True to US kids age sizing (2–7), cut roomy for layering.', pos: 50 },
    rows: [['2', '26', '15'], ['3', '27', '16'], ['4', '28', '17'], ['5/6', '30', '18'], ['7', '31', '19']],
  },
  toddler: {
    label: 'Toddler tee',
    blank: 'Rabbit Skins 3321',
    fit: { tag: 'True to size', note: 'True to US toddler age sizing (2–5/6).', pos: 50 },
    rows: [['2 (2T)', '20–22', '15.5'], ['3 (3T)', '22–24', '16.5'], ['4 (4T)', '24–27', '17.5'], ['5/6', '27–30', '18.5']],
  },
  toddler_fleece: {
    label: 'Toddler hoodie',
    blank: 'Rabbit Skins 3326',
    fit: { tag: 'True to size', note: 'True to US toddler age sizing, cut roomy for layering.', pos: 52 },
    rows: [['2T', '29', '15.6'], ['4T', '31', '17.6'], ['5/6T', '33', '18.6']],
  },
  womens: {
    label: "Women's v-neck",
    blank: 'Bella+Canvas 6405CVC',
    fit: { tag: 'Relaxed', note: 'Relaxed cut with a slightly longer body. True to size.', pos: 60 },
    rows: [['S', '34–37', '25'], ['M', '38–41', '25.75'], ['L', '42–45', '26.5'], ['XL', '46–49', '27.25'], ['2XL', '50–53', '28']],
  },
  baby: {
    label: 'Baby onesie',
    blank: 'Bella+Canvas 100B',
    fit: { tag: 'True to size', note: 'True to US infant age sizing (3–6m fits 12–16 lbs, 6–12m 16–22, 12–18m 22–27). Between sizes? Size up for longer wear.', pos: 50 },
    rows: null,
  },
};
const LIG_BB_CAT_LABEL = { adult: 'Adult', kids: 'Kids', toddler: 'Toddler', womens: "Women's", baby: 'Baby' };
function ligBbCategory(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('baby') || t.includes('one-piece')) return 'baby';
  if (t.includes('youth') || t.includes('kids')) return 'kids';
  if (t.includes('toddler')) return 'toddler';
  if (t.includes('women')) return 'womens';
  return 'adult';
}
function ligBbChartKey(type) {
  const t = String(type || '').toLowerCase();
  const cat = ligBbCategory(type);
  const fleece = t.includes('sweatshirt') || t.includes('hoodie') || t.includes('crewneck');
  if (cat === 'adult' && fleece) return t.includes('hoodie') ? 'adult_hoodie' : 'adult_crew';
  if (cat === 'kids' && fleece) return t.includes('youth') ? 'kids_fleece' : 'kids_crew_little';
  if (cat === 'toddler' && fleece) return 'toddler_fleece';
  return cat;
}
const LIG_FB_GARMENT_LABEL = {
  't-shirts': 'Tee',
  'long-sleeve tees': 'Long Sleeve',
  'crewneck sweatshirts': 'Crewneck',
  'hoodies': 'Hoodie',
  "women's v-neck tees": "V-Neck",
  'youth t-shirts': 'Youth Tee',
  'youth sweatshirts': 'Youth Crewneck',
  'kids sweatshirts': 'Little Kids Crew',
  'toddler t-shirts': 'Toddler Tee',
  'toddler hoodies': 'Toddler Hoodie',
  'baby one-pieces': 'Onesie',
  'hooded long-sleeve tees': 'Hooded Long Sleeve',
};
function ligFbGarmentLabel(type) {
  return LIG_FB_GARMENT_LABEL[String(type || '').toLowerCase()] || String(type || 'Piece');
}
/** Slot type -> ordered garment menu (by Shopify product.type). */
const LIG_FB_SLOT_GARMENTS = {
  adult: ['T-Shirts', 'Long-Sleeve Tees', 'Crewneck Sweatshirts', 'Hoodies'],
  womens: ["Women's V-Neck Tees", 'T-Shirts', 'Long-Sleeve Tees', 'Crewneck Sweatshirts', 'Hoodies'],
  kid: ['Youth T-Shirts', 'Youth Sweatshirts', 'Kids Sweatshirts'],
  toddler: ['Toddler T-Shirts', 'Toddler Hoodies', 'Kids Sweatshirts'],
  baby: ['Baby One-Pieces'],
};
/** Person picker for "+ Add another". */
const LIG_FB_PERSONS = [
  { label: 'Adult', type: 'adult' },
  { label: "Women's", type: 'womens' },
  { label: 'Kid', type: 'kid' },
  { label: 'Toddler', type: 'toddler' },
  { label: 'Baby', type: 'baby' },
  { label: 'Grandpa', type: 'adult' },
  { label: 'Grandma', type: 'womens' },
];
const LIG_FB_STORAGE_KEY = 'lig-fb-roster-v1';

function ligFbTrack(event, data) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 'lig_fb_' + event }, data || {}));
  } catch { /* analytics must never break the builder */ }
}

class LigFamilyBuilder extends HTMLElement {
  /** @type {Array<any>} */
  #products = [];
  /** @type {Map<number, any>} */
  #byId = new Map();
  /** [{key,label,image}] unique prints in the pool */
  #designs = [];
  /** active print key or null */
  #design = null;
  /** [{id,label,type,base:boolean,entry:{productId,variantId}|null,needsColor:boolean,size:string|null}] */
  #slots = [];
  #slotSeq = 0;
  /** @type {Array<{min:number,pct:number}>} */
  #tiers = [];
  #discounts = false;
  #maxItems = 12;
  /** per-product currently-selected variant id (persists across drawer opens) */
  #selectedVariant = new Map();
  /** drawer context: which slot is being dressed (null = browse mode) */
  #drawerSlotId = null;
  #openProductId = null;
  #zoomed = false;
  #money;
  #pinch = { scale: 1, startDist: 0 };
  #gallerySlides = [];
  #galleryIndex = 0;
  #galleryRaf = 0;
  #galleryAnimating = false;
  #undoTimer = 0;
  #undoSnapshot = null;
  #toastTimer = 0;
  /** Fallback name->hex map. The authoritative source is the shop's
   *  custom.color_swatches metafield (emitted as a data island); these cover
   *  anything the metafield misses. Hexes matched to Bella+Canvas /
   *  Rabbit Skins swatch cards so the chips read true to the garment. */
  #colorMap = {
    navy: '#212a3c',
    truenavy: '#243352',
    heathermidnightnavy: '#31394d',
    blackheather: '#1e1e21',
    black: '#1d1d1f',
    vintageblack: '#2b2b2d',
    solidblackblend: '#222225',
    darkgreyheather: '#3f4147',
    darkgrey: '#3e3c3d',
    darkheather: '#454549',
    heathernavy: '#34405c',
    heatherslate: '#6b7480',
    navyblazer: '#23304a',
    heatherforest: '#2c4a3d',
    heatheremerald: '#23594c',
    forest: '#1f3c2f',
    agave: '#5a6e5f',
    militarygreen: '#4a5240',
    brown: '#4a3527',
    heathernatural: '#e7e0cf',
    babyblue: '#bcd9ea',
    solidwhiteblend: '#f4f4f1',
    white: '#f6f6f4',
    athleticheather: '#cdd1d6',
    natural: '#ece6d6',
    softpink: '#f1d2d7',
    oceanblue: '#3f78a6',
    turquoise: '#53c4bf',
    maroon: '#6e2b3a',
    royal: '#2d5fa6',
    kelly: '#2f7d4f',
    red: '#b03a3a',
  };
  /** name(normalized) -> hex from the shop's custom.color_swatches metafield. */
  #swatchMap = new Map();

  connectedCallback() {
    this.#maxItems = Math.max(2, Number(this.dataset.maxItems) || 12);
    this.#discounts = this.dataset.discountsEnabled === 'true';
    this.#products = this.#parseProducts();
    if (!this.#products.length) {
      this.setAttribute('hidden', '');
      return;
    }
    for (const p of this.#products) {
      if (p && p.id != null) this.#byId.set(Number(p.id), p);
    }
    this.#tiers = this.#discounts ? this.#parseTiers() : [];
    for (const p of this.#products) {
      const v = this.#defaultVariant(p);
      if (v) this.#selectedVariant.set(Number(p.id), Number(v.id));
    }
    // Authoritative swatch hexes (shop's custom.color_swatches metafield).
    try {
      const node = this.querySelector('[data-fb-swatches]');
      const map = node ? JSON.parse(node.textContent) : null;
      if (map && typeof map === 'object') {
        for (const [name, hex] of Object.entries(map)) {
          if (typeof hex === 'string' && hex) this.#swatchMap.set(this.#norm(name), hex);
        }
      }
    } catch { /* fall back to the static map */ }
    this.#designs = this.#designsFromProducts();

    // Progressive family: the lineup starts EMPTY — nothing about the family's
    // shape is presumed. People are added one at a time via the person picker.
    this.#slots = [];

    this.#money = this.#moneyFormatter();
    this.addEventListener('click', this.#handleClick);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    this.elGalleryTrack = this.querySelector('[data-fb-gallery-track]');
    if (this.elGalleryTrack instanceof HTMLElement) {
      this.elGalleryTrack.addEventListener('scroll', this.#onGalleryScroll, { passive: true });
    }

    // Restore an in-progress roster, then let URL params override the print.
    this.#restore();
    let scrollToBuilder = false;
    try {
      const params = new URLSearchParams(window.location.search);
      const want = params.get('design') || params.get('print');
      if (want && this.#designs.some((d) => d.key === want)) {
        if (this.#design && this.#design !== want && this.#hasFilled()) this.#remapToDesign(want, { silent: true });
        this.#design = want;
        scrollToBuilder = true;
      }
      // ?browse=1 (the landing's "just want one" links) opens the piece grid.
      if (params.get('browse')) {
        const browse = this.querySelector('[data-fb-browse]');
        if (browse instanceof HTMLDetailsElement) browse.open = true;
      }
    } catch { /* no-op */ }
    if (!this.#design && this.#designs.length === 1) this.#design = this.#designs[0].key;

    this.#renderChooser();
    this.#syncDesignUi();
    this.#renderLineup();
    this.#renderBar();
    this.setAttribute('data-rendered', 'true');
    this.removeAttribute('hidden');
    if (scrollToBuilder) {
      requestAnimationFrame(() => this.scrollIntoView({ behavior: 'auto', block: 'start' }));
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    if (this.elGalleryTrack instanceof HTMLElement) {
      this.elGalleryTrack.removeEventListener('scroll', this.#onGalleryScroll);
    }
  }

  /* --- Parsing ---------------------------------------------------------- */

  #parseProducts() {
    try {
      const node = this.querySelector('[data-fb-products]');
      const data = node ? JSON.parse(node.textContent) : [];
      return Array.isArray(data) ? data.filter((p) => p && p.id != null) : [];
    } catch {
      return [];
    }
  }

  #parseTiers() {
    let raw = [];
    try {
      raw = JSON.parse(this.dataset.tiers || '[]');
    } catch {
      raw = [];
    }
    if (!Array.isArray(raw)) raw = [];
    return raw
      .map((t) => ({ min: Number(t && t.min), pct: Number(t && t.pct) }))
      .filter((t) => Number.isFinite(t.min) && Number.isFinite(t.pct) && t.min > 0 && t.pct > 0)
      .sort((a, b) => a.min - b.min);
  }

  /* --- Design layer ------------------------------------------------------ */

  /** Unique designs in the pool. The section's "design_meta" setting
   *  (key|Name|macroURL per line) overrides the derived label and provides the
   *  macro close-up used by the chooser cards + the Wearing chip. */
  #designsFromProducts() {
    const meta = new Map();
    try {
      const node = this.querySelector('[data-fb-design-meta]');
      if (node) {
        for (const line of String(node.textContent || '').split('\n')) {
          const parts = line.split('|').map((s) => s.trim());
          if (parts[0]) meta.set(parts[0], { label: parts[1] || null, macro: parts[2] || null });
        }
      }
    } catch { /* meta is optional */ }
    const map = new Map();
    for (const p of this.#products) {
      const key = String(p.design || '').trim();
      if (!key) continue;
      if (!map.has(key)) map.set(key, { key, label: this.#designLabel(p), image: p.image || '', macro: null });
      const d = map.get(key);
      if (/^t-shirts$/i.test(String(p.type || ''))) {
        d.image = p.image || d.image;
        d.label = this.#designLabel(p);
      }
    }
    for (const [key, m] of meta) {
      const d = map.get(key);
      if (!d) continue;
      if (m.label) d.label = m.label;
      if (m.macro) d.macro = m.macro;
    }
    return [...map.values()];
  }

  #designLabel(p) {
    let t = String(p.title || '').trim();
    const label = ligFbGarmentLabel(p.type);
    if (label && t.toLowerCase().endsWith(label.toLowerCase())) {
      t = t.slice(0, t.length - label.length).trim();
    }
    t = t.replace(/\s+(Tee|Crewneck|Hoodie|Long Sleeve|Onesie|V-Neck)$/i, '').trim();
    return t || String(p.title || '');
  }

  /** Product for (design, garment type), or null. */
  #productFor(designKey, type) {
    return (
      this.#products.find(
        (p) => String(p.design || '') === designKey && String(p.type || '').toLowerCase() === String(type).toLowerCase()
      ) || null
    );
  }

  /** Rail affordance: show edge fades + paging arrows only when the chooser
   *  actually overflows, and hide each arrow at its end of the rail. */
  #syncChooserRail() {
    const shell = this.querySelector('[data-fb-chooser-shell]');
    const row = this.querySelector('[data-fb-chooser-row]');
    if (!(shell instanceof HTMLElement) || !(row instanceof HTMLElement)) return;
    const overflows = row.scrollWidth > row.clientWidth + 4;
    const atStart = row.scrollLeft < 8;
    const atEnd = row.scrollLeft + row.clientWidth > row.scrollWidth - 8;
    shell.classList.toggle('has-left', overflows && !atStart);
    shell.classList.toggle('has-right', overflows && !atEnd);
    const prev = shell.querySelector('[data-fb-rail-prev]');
    const next = shell.querySelector('[data-fb-rail-next]');
    if (prev instanceof HTMLElement) prev.hidden = !overflows || atStart;
    if (next instanceof HTMLElement) next.hidden = !overflows || atEnd;
  }

  #renderChooser() {
    const row = this.querySelector('[data-fb-chooser-row]');
    if (!(row instanceof HTMLElement)) return;
    const frag = document.createDocumentFragment();
    for (const d of this.#designs) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lig-fb__chooser-card';
      btn.setAttribute('data-fb-choose-print', '');
      btn.dataset.key = d.key;
      const face = d.macro || d.image;
      if (face) {
        const img = document.createElement('img');
        img.className = 'lig-fb__chooser-img';
        img.src = this.#withDetail(face, 440);
        img.alt = d.label;
        img.loading = 'lazy';
        img.width = 160;
        img.height = 160;
        btn.appendChild(img);
      }
      const name = document.createElement('span');
      name.className = 'lig-fb__chooser-name';
      name.textContent = d.label;
      btn.appendChild(name);
      frag.appendChild(btn);
    }
    row.replaceChildren(frag);
    // Rail affordance: sync now (after images size the row) + on scroll/resize.
    if (!row.dataset.railBound) {
      row.dataset.railBound = '1';
      row.addEventListener('scroll', () => this.#syncChooserRail(), { passive: true });
      window.addEventListener('resize', () => this.#syncChooserRail());
    }
    requestAnimationFrame(() => this.#syncChooserRail());
    setTimeout(() => this.#syncChooserRail(), 400); // lazy imgs settle the width
  }

  #syncDesignUi() {
    const design = this.#designs.find((d) => d.key === this.#design) || null;
    const chip = this.querySelector('[data-fb-print-chip]');
    const chooser = this.querySelector('[data-fb-chooser]');
    const lineup = this.querySelector('[data-fb-lineup]');
    // The compact chip is superseded by the large print STAGE; keep it hidden.
    if (chip instanceof HTMLElement) chip.hidden = true;
    const stage = this.querySelector('[data-fb-stage]');
    if (stage instanceof HTMLElement) {
      stage.hidden = !design;
      if (design) {
        const img = stage.querySelector('[data-fb-stage-img]');
        const name = stage.querySelector('[data-fb-stage-name]');
        const face = design.macro || design.image;
        if (img instanceof HTMLImageElement && face) {
          img.src = this.#withDetail(face, 900);
          // Pre-warm the lightbox rendition so "Print detail" opens instantly.
          try { new Image().src = this.#withDetail(face, 1400); } catch { /* no-op */ }
          img.alt = design.label;
          img.dataset.full = face;
        }
        if (name instanceof HTMLElement) name.textContent = design.label;
      }
    }
    if (chooser instanceof HTMLElement) {
      chooser.hidden = !!design;
      chooser.querySelectorAll('[data-fb-choose-print]').forEach((b) => {
        b.classList.toggle('is-selected', b.dataset.key === this.#design);
      });
    }
    if (lineup instanceof HTMLElement) lineup.classList.toggle('is-dimmed', !design);
    const hint = this.querySelector('[data-fb-lineup-hint]');
    if (hint instanceof HTMLElement) {
      hint.textContent = design
        ? hint.dataset.hintReady || 'Start with anyone. Tap a person to dress them.'
        : hint.dataset.hintPrintless || 'Pick a print above, then tap a person to dress them.';
    }
    // Browse accordion filtering.
    this.querySelectorAll('[data-fb-browse-row]').forEach((r) => {
      r.hidden = !!this.#design && r.dataset.design !== this.#design;
    });
  }

  #setDesign(key, opts = {}) {
    if (key === this.#design) {
      this.#syncDesignUi();
      return;
    }
    const had = this.#hasFilled();
    if (had && this.#design) {
      this.#remapToDesign(key, {});
      ligFbTrack('print_switch', { to: key });
    } else {
      this.#design = key;
      ligFbTrack('print_select', { key });
    }
    this.#syncDesignUi();
    this.#renderLineup();
    this.#renderBar();
    this.#persist();
    if (opts.scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Re-dress every filled slot in the new print. Sizes carry; color misses flag. */
  #remapToDesign(newKey, opts = {}) {
    let misses = 0;
    for (const slot of this.#slots) {
      if (!slot.entry) continue;
      const oldProduct = this.#byId.get(slot.entry.productId);
      if (!oldProduct) continue;
      const newProduct = this.#productFor(newKey, oldProduct.type);
      if (!newProduct) {
        // Matrix should be 1:1; if not, keep the person but require re-dress.
        slot.entry = null;
        slot.needsColor = false;
        continue;
      }
      const oldVariant = (oldProduct.variants || []).find((v) => Number(v.id) === slot.entry.variantId);
      const sizeIdx = this.#sizeOptionIndex(oldProduct);
      const sizeKey = `option${sizeIdx + 1}`;
      const size = oldVariant ? String(oldVariant[sizeKey]) : null;
      const colorIdx = this.#colorOptionIndex(oldProduct);
      const colorKey = colorIdx >= 0 ? `option${colorIdx + 1}` : null;
      const color = oldVariant && colorKey ? String(oldVariant[colorKey]) : null;

      // Same size + same color in the new print?
      const newSizeIdx = this.#sizeOptionIndex(newProduct);
      const newSizeKey = `option${newSizeIdx + 1}`;
      const newColorIdx = this.#colorOptionIndex(newProduct);
      const newColorKey = newColorIdx >= 0 ? `option${newColorIdx + 1}` : null;
      let match = null;
      if (size) {
        match = (newProduct.variants || []).find(
          (v) =>
            String(v[newSizeKey]) === size &&
            (!color || !newColorKey || String(v[newColorKey]) === color) &&
            v.available
        );
      }
      if (match) {
        slot.entry = { productId: Number(newProduct.id), variantId: Number(match.id) };
        slot.needsColor = false;
        this.#selectedVariant.set(Number(newProduct.id), Number(match.id));
      } else {
        // Keep the person + size; ask for a new color.
        const sameSize = (newProduct.variants || []).find((v) => String(v[newSizeKey]) === size && v.available);
        slot.entry = { productId: Number(newProduct.id), variantId: null };
        slot.size = size;
        slot.needsColor = true;
        if (sameSize) this.#selectedVariant.set(Number(newProduct.id), Number(sameSize.id));
        misses += 1;
      }
    }
    this.#design = newKey;
    if (!opts.silent) {
      const label = (this.#designs.find((d) => d.key === newKey) || {}).label || 'the new print';
      this.#toast(
        misses > 0
          ? `Switched to ${label}. Your sizes carried over; ${misses === 1 ? 'one piece needs' : misses + ' pieces need'} a new color.`
          : `Switched to ${label}. Your sizes carried over.`
      );
    }
  }

  /* --- Lineup rendering --------------------------------------------------- */

  #slotById(id) {
    return this.#slots.find((s) => s.id === Number(id)) || null;
  }

  #filledCount() {
    return this.#slots.filter((s) => s.entry && s.entry.variantId).length;
  }

  #pieceCount() {
    return this.#slots.filter((s) => s.entry).length;
  }

  #hasFilled() {
    return this.#slots.some((s) => s.entry);
  }

  #renderLineup() {
    const lineup = this.querySelector('[data-fb-lineup]');
    if (!(lineup instanceof HTMLElement)) return;

    // Remove any added-slot elements no longer in state; append missing ones.
    const stateIds = new Set(this.#slots.map((s) => String(s.id)));
    lineup.querySelectorAll('[data-fb-slot]').forEach((el) => {
      if (!stateIds.has(el.dataset.slotId || '')) el.remove();
    });
    const addCard = lineup.querySelector('[data-fb-add-slot]');
    for (const slot of this.#slots) {
      let el = lineup.querySelector(`[data-fb-slot][data-slot-id="${slot.id}"]`);
      if (!el) {
        el = this.#buildSlotEl(slot);
        if (addCard) lineup.insertBefore(el, addCard);
        else lineup.appendChild(el);
      }
      this.#paintSlot(el, slot);
    }

    // Cap note + add-card state.
    const capped = this.#pieceCount() >= this.#maxItems;
    const capNote = this.querySelector('[data-fb-cap-note]');
    if (capNote instanceof HTMLElement) capNote.hidden = !capped;
    const resetBtn = this.querySelector('[data-fb-reset]');
    if (resetBtn instanceof HTMLElement) resetBtn.hidden = this.#slots.length === 0;
    if (addCard instanceof HTMLElement) {
      const btn = addCard.querySelector('[data-fb-add-btn]');
      if (btn instanceof HTMLButtonElement) btn.disabled = capped;
      addCard.classList.toggle('is-disabled', capped);
      // Progressive label: "Start your family" -> "Add the next person".
      const label = addCard.querySelector('[data-fb-add-label]');
      if (label instanceof HTMLElement) {
        label.textContent = this.#slots.length === 0
          ? label.dataset.labelStart || 'Start your family'
          : label.dataset.labelNext || 'Add the next person';
      }
      addCard.classList.toggle('is-start', this.#slots.length === 0);
    }
  }

  #buildSlotEl(slot) {
    const li = document.createElement('li');
    li.className = 'lig-fb__slot is-empty';
    li.setAttribute('data-fb-slot', '');
    li.dataset.slotId = String(slot.id);
    li.dataset.slotType = slot.type;
    li.dataset.slotLabel = slot.label;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lig-fb__slot-btn';
    btn.setAttribute('data-fb-slot-btn', '');
    btn.setAttribute('aria-label', `Dress ${slot.label}`);
    const media = document.createElement('span');
    media.className = 'lig-fb__slot-media';
    media.setAttribute('data-fb-slot-media', '');
    media.innerHTML =
      '<svg class="lig-fb__slot-silhouette" viewBox="0 0 80 96" aria-hidden="true"><circle cx="40" cy="26" r="15"/><path d="M14 92c0-21 11-32 26-32s26 11 26 32z"/></svg><span class="lig-fb__slot-plus" aria-hidden="true">+</span>';
    const name = document.createElement('span');
    name.className = 'lig-fb__slot-label';
    name.setAttribute('data-fb-slot-name', '');
    name.textContent = slot.label;
    const sub = document.createElement('span');
    sub.className = 'lig-fb__slot-sub';
    sub.setAttribute('data-fb-slot-sub', '');
    btn.append(media, name, sub);
    li.appendChild(btn);
    const controls = document.createElement('span');
    controls.className = 'lig-fb__slot-controls';
    controls.setAttribute('data-fb-slot-controls', '');
    controls.hidden = true;
    controls.innerHTML =
      '<button type="button" class="lig-fb__slot-ctl" data-fb-slot-edit aria-label="Edit this piece"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true"><path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<button type="button" class="lig-fb__slot-ctl lig-fb__slot-ctl--x" data-fb-slot-x aria-label="Remove"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>';
    li.appendChild(controls);
    return li;
  }

  /** Paint one slot element from its state (empty / filled / needs-color). */
  #paintSlot(el, slot) {
    const media = el.querySelector('[data-fb-slot-media]');
    const sub = el.querySelector('[data-fb-slot-sub]');
    const name = el.querySelector('[data-fb-slot-name]');
    if (name instanceof HTMLElement) name.textContent = slot.label;
    const filled = !!slot.entry;
    el.classList.toggle('is-empty', !filled);
    el.classList.toggle('is-filled', filled && !slot.needsColor);
    el.classList.toggle('is-needs-color', !!slot.needsColor);
    // The X removes a PERSON, dressed or not (an empty "Grandpa" card must be
    // clearable); the pencil only makes sense once there's a piece to edit.
    const controls = el.querySelector('[data-fb-slot-controls]');
    if (controls instanceof HTMLElement) controls.hidden = false;
    const editCtl = el.querySelector('[data-fb-slot-edit]');
    if (editCtl instanceof HTMLElement) editCtl.hidden = !filled;
    if (!(media instanceof HTMLElement)) return;

    const oldImg = media.querySelector('.lig-fb__slot-img');
    if (!filled) {
      if (oldImg) oldImg.remove();
      if (sub instanceof HTMLElement) sub.textContent = '';
      return;
    }
    const product = this.#byId.get(slot.entry.productId);
    if (!product) return;
    const variant = slot.entry.variantId
      ? (product.variants || []).find((v) => Number(v.id) === slot.entry.variantId)
      : null;

    // Color-correct image: first image matching the variant's color, else featured.
    let src = product.image || '';
    if (variant) {
      const ci = this.#colorOptionIndex(product);
      const color = ci >= 0 ? this.#norm(variant[`option${ci + 1}`]) : null;
      if (color && Array.isArray(product.images)) {
        const m = product.images.find((im) => {
          const meta = this.#imageMeta(im.alt);
          return meta.color && this.#norm(meta.color) === color;
        });
        if (m) src = m.url;
      }
    }
    if (src) {
      let img = oldImg;
      if (!(img instanceof HTMLImageElement)) {
        img = document.createElement('img');
        img.className = 'lig-fb__slot-img';
        img.loading = 'lazy';
        img.alt = '';
        media.appendChild(img);
      }
      const next = this.#withWidth(src, 400);
      if (img.src !== next) {
        img.classList.remove('is-in');
        img.src = next;
        requestAnimationFrame(() => requestAnimationFrame(() => img.classList.add('is-in')));
      } else {
        img.classList.add('is-in');
      }
    }
    if (sub instanceof HTMLElement) {
      if (slot.needsColor) {
        sub.textContent = 'Pick a new color';
      } else if (variant) {
        const bits = [ligFbGarmentLabel(product.type)];
        const label = this.#variantLabel(product, variant);
        if (label) bits.push(label);
        sub.textContent = bits.join(' · ');
      } else {
        sub.textContent = ligFbGarmentLabel(product.type);
      }
    }
  }

  /* --- Mini-sheets --------------------------------------------------------- */

  #openSheet(sel) {
    const sheet = this.querySelector(sel);
    const scrim = this.querySelector('[data-fb-sheetscrim]');
    if (!(sheet instanceof HTMLElement)) return;
    sheet.hidden = false;
    if (scrim instanceof HTMLElement) scrim.hidden = false;
    requestAnimationFrame(() => sheet.classList.add('is-open'));
  }

  #closeSheets() {
    this.querySelectorAll('[data-fb-garment-sheet], [data-fb-person-sheet], [data-fb-action-sheet]').forEach((s) => {
      s.classList.remove('is-open');
      s.hidden = true;
    });
    const scrim = this.querySelector('[data-fb-sheetscrim]');
    if (scrim instanceof HTMLElement) scrim.hidden = true;
  }

  #openGarmentSheet(slot) {
    if (!this.#design) return;
    const sheet = this.querySelector('[data-fb-garment-sheet]');
    const title = this.querySelector('[data-fb-garment-title]');
    const list = this.querySelector('[data-fb-garment-list]');
    if (!(sheet instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
    const garments = (LIG_FB_SLOT_GARMENTS[slot.type] || LIG_FB_SLOT_GARMENTS.adult)
      .map((t) => this.#productFor(this.#design, t))
      .filter(Boolean);
    if (!garments.length) return;
    // Baby (or any single-garment person) skips the sheet entirely.
    if (garments.length === 1) {
      this.#openDrawer(garments[0].id, slot.id);
      return;
    }
    if (title instanceof HTMLElement) title.textContent = `What's ${slot.label} wearing?`;
    const frag = document.createDocumentFragment();
    for (const p of garments) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lig-fb__minisheet-item';
      btn.setAttribute('data-fb-garment-pick', '');
      btn.dataset.id = String(p.id);
      btn.dataset.slot = String(slot.id);
      const name = document.createElement('span');
      name.textContent = ligFbGarmentLabel(p.type);
      const price = document.createElement('span');
      price.className = 'lig-fb__minisheet-price';
      price.textContent = this.#money(p.price);
      btn.append(name, price);
      li.appendChild(btn);
      frag.appendChild(li);
    }
    list.replaceChildren(frag);
    sheet.dataset.slot = String(slot.id);
    this.#openSheet('[data-fb-garment-sheet]');
    ligFbTrack('slot_open', { type: slot.type });
  }

  #openPersonSheet() {
    const list = this.querySelector('[data-fb-person-list]');
    if (!(list instanceof HTMLElement)) return;
    // Progressive prompts: "Who are we dressing first?" then "Who is next?",
    // with a "That's everyone" exit once the family has begun.
    const first = this.#slots.length === 0;
    const title = this.querySelector('[data-fb-person-title]');
    if (title instanceof HTMLElement) {
      title.textContent = first
        ? title.dataset.titleFirst || 'Who are we dressing first?'
        : title.dataset.titleNext || 'Who is next?';
    }
    const done = this.querySelector('[data-fb-person-done]');
    if (done instanceof HTMLElement) done.hidden = first;
    const frag = document.createDocumentFragment();
    for (const person of LIG_FB_PERSONS) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lig-fb__minisheet-item';
      btn.setAttribute('data-fb-person-pick', '');
      btn.dataset.type = person.type;
      btn.dataset.label = person.label;
      btn.textContent = person.label;
      li.appendChild(btn);
      frag.appendChild(li);
    }
    list.replaceChildren(frag);
    this.#openSheet('[data-fb-person-sheet]');
  }

  #openActionSheet(slot) {
    const sheet = this.querySelector('[data-fb-action-sheet]');
    const title = this.querySelector('[data-fb-action-title]');
    if (!(sheet instanceof HTMLElement)) return;
    const renameRow = this.querySelector('[data-fb-rename-row]');
    if (renameRow instanceof HTMLElement) renameRow.hidden = true;
    const product = slot.entry ? this.#byId.get(slot.entry.productId) : null;
    if (title instanceof HTMLElement) {
      title.textContent = product ? `${slot.label} · ${ligFbGarmentLabel(product.type)}` : slot.label;
    }
    sheet.dataset.slot = String(slot.id);
    this.#openSheet('[data-fb-action-sheet]');
  }

  /* --- Slot mutations ------------------------------------------------------ */

  #addPerson(type, baseLabel) {
    if (this.#pieceCount() >= this.#maxItems) return;
    const existing = this.#slots.filter((s) => s.label === baseLabel || s.label.startsWith(baseLabel + ' ')).length;
    const label = existing > 0 ? `${baseLabel} ${existing + 1}` : baseLabel;
    this.#slotSeq += 1;
    // fresh = just added via the progressive flow; after their piece lands we
    // prompt "Who is next?" exactly once.
    const slot = { id: this.#slotSeq, label, type, fresh: true, entry: null, needsColor: false, size: null };
    this.#slots.push(slot);
    this.#renderLineup();
    this.#persist();
    // Straight into dressing them.
    this.#openGarmentSheet(slot);
  }

  #removeSlot(slot) {
    const idx = this.#slots.findIndex((s) => s.id === slot.id);
    if (idx < 0) return;
    this.#undoSnapshot = { slot: JSON.parse(JSON.stringify(slot)), index: idx };
    this.#slots.splice(idx, 1);
    this.#renderLineup();
    this.#renderBar();
    this.#persist();
    this.#showUndo(`${slot.label} removed.`);
  }

  /** "Start over": clears every person (print choice stays). Undo restores
   *  the whole roster. */
  #resetAll() {
    if (!this.#slots.length) return;
    this.#undoSnapshot = { slots: JSON.parse(JSON.stringify(this.#slots)) };
    this.#slots = [];
    this.#renderLineup();
    this.#renderBar();
    this.#persist();
    this.#showUndo('Set cleared.');
  }

  #showUndo(text) {
    const bar = this.querySelector('[data-fb-undo]');
    const label = this.querySelector('[data-fb-undo-text]');
    if (!(bar instanceof HTMLElement)) return;
    if (label instanceof HTMLElement) label.textContent = text;
    bar.hidden = false;
    clearTimeout(this.#undoTimer);
    this.#undoTimer = window.setTimeout(() => {
      bar.hidden = true;
      this.#undoSnapshot = null;
    }, 5000);
  }

  #undo() {
    const snap = this.#undoSnapshot;
    const bar = this.querySelector('[data-fb-undo]');
    if (bar instanceof HTMLElement) bar.hidden = true;
    clearTimeout(this.#undoTimer);
    if (!snap) return;
    if (Array.isArray(snap.slots)) {
      // Start-over undo: bring the whole roster back.
      this.#slots = snap.slots;
    } else {
      const existing = this.#slots.find((s) => s.id === snap.slot.id);
      if (existing) {
        Object.assign(existing, snap.slot);
      } else {
        this.#slots.splice(Math.min(snap.index, this.#slots.length), 0, snap.slot);
      }
    }
    this.#undoSnapshot = null;
    this.#renderLineup();
    this.#renderBar();
    this.#persist();
  }

  #toast(text) {
    const el = this.querySelector('[data-fb-toast]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = text;
    el.hidden = false;
    el.classList.add('is-on');
    clearTimeout(this.#toastTimer);
    this.#toastTimer = window.setTimeout(() => {
      el.classList.remove('is-on');
      el.hidden = true;
    }, 3500);
  }

  /* --- Persistence ---------------------------------------------------------- */

  #persist() {
    try {
      const data = {
        design: this.#design,
        slots: this.#slots.map((s) => ({
          label: s.label,
          type: s.type,
          productId: s.entry ? s.entry.productId : null,
          variantId: s.entry ? s.entry.variantId : null,
          needsColor: s.needsColor,
          size: s.size,
        })),
      };
      localStorage.setItem(LIG_FB_STORAGE_KEY, JSON.stringify(data));
    } catch { /* storage may be unavailable; never break the page */ }
  }

  #restore() {
    try {
      const raw = localStorage.getItem(LIG_FB_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || !Array.isArray(data.slots)) return;
      if (data.design && this.#designs.some((d) => d.key === data.design)) this.#design = data.design;
      for (const s of data.slots) {
        // All slots are dynamic now; skip empty legacy preset slots so old
        // rosters do not resurrect a presumed family shape.
        if (s.base && !s.productId) continue;
        this.#slotSeq += 1;
        const slot = { id: this.#slotSeq, label: s.label || 'Someone', type: s.type || 'adult', fresh: false, entry: null, needsColor: false, size: null };
        this.#slots.push(slot);
        if (s.productId && this.#byId.has(Number(s.productId))) {
          const product = this.#byId.get(Number(s.productId));
          const variant = s.variantId
            ? (product.variants || []).find((v) => Number(v.id) === Number(s.variantId))
            : null;
          if (variant) {
            slot.entry = { productId: Number(s.productId), variantId: Number(variant.id) };
            slot.needsColor = false;
            this.#selectedVariant.set(Number(s.productId), Number(variant.id));
          } else if (s.needsColor) {
            slot.entry = { productId: Number(s.productId), variantId: null };
            slot.needsColor = true;
            slot.size = s.size || null;
          }
        }
      }
    } catch { /* corrupted storage: start fresh */ }
  }

  /* --- Click delegation ------------------------------------------------------ */

  /** Intercept print-gallery CTAs anywhere on the page (no-reload set + scroll),
   *  plus gallery zoom buttons and "shop singles" links. */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Zoom-to-detail from anywhere (gallery cards carry data-fb-zoom="<img url>").
    const zoom = target.closest('[data-fb-zoom]');
    if (zoom && !this.contains(zoom)) {
      event.preventDefault();
      this.#openLightbox(zoom.getAttribute('data-fb-zoom') || '', zoom.getAttribute('data-fb-zoom-caption') || '');
      return;
    }

    // "Just want one" → set the print, open the browse grid, take them there.
    const singles = target.closest('[data-fb-shop-singles]');
    if (singles && !this.contains(singles)) {
      event.preventDefault();
      const skey = singles.getAttribute('data-fb-shop-singles') || '';
      if (this.#designs.some((d) => d.key === skey)) this.#setDesign(skey, {});
      const browse = this.querySelector('[data-fb-browse]');
      if (browse instanceof HTMLDetailsElement) {
        browse.open = true;
        browse.scrollIntoView({ behavior: 'smooth', block: 'start' });
        ligFbTrack('browse_open', { via: 'singles' });
      }
      return;
    }

    const link = target.closest('[data-fb-set-print]');
    if (!link || this.contains(link)) return;
    const key = link.getAttribute('data-fb-set-print') || '';
    if (!this.#designs.some((d) => d.key === key)) return;
    event.preventDefault();
    this.#setDesign(key, { scroll: true });
  };

  /* --- Print-detail lightbox --------------------------------------------- */

  #openLightbox(src, caption) {
    const box = this.querySelector('[data-fb-lightbox]');
    if (!(box instanceof HTMLElement) || !src) return;
    const img = box.querySelector('[data-fb-lightbox-img]');
    const cap = box.querySelector('[data-fb-lightbox-caption]');
    if (img instanceof HTMLImageElement) img.src = this.#withDetail(src, 1400);
    if (cap instanceof HTMLElement) cap.textContent = caption || '';
    box.hidden = false;
    requestAnimationFrame(() => box.classList.add('is-open'));
    document.documentElement.classList.add('lig-fb-locked');
  }

  #closeLightbox() {
    const box = this.querySelector('[data-fb-lightbox]');
    if (!(box instanceof HTMLElement) || box.hidden) return;
    box.classList.remove('is-open');
    box.hidden = true;
    document.documentElement.classList.remove('lig-fb-locked');
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-fb-sizeguide-close]')) {
      event.preventDefault();
      this.#closeSizeGuide();
      return;
    }
    if (target.closest('[data-fb-size-guide]')) {
      event.preventDefault();
      this.#openSizeGuide();
      return;
    }
    if (target.closest('[data-fb-scrim]') || target.closest('[data-fb-drawer-close]')) {
      event.preventDefault();
      this.#closeDrawer();
      return;
    }
    if (target.closest('[data-fb-lightbox-close]')) {
      event.preventDefault();
      this.#closeLightbox();
      return;
    }
    // Stage tap → detail lightbox of the active print.
    if (target.closest('[data-fb-stage-zoom]')) {
      event.preventDefault();
      const img = this.querySelector('[data-fb-stage-img]');
      const name = this.querySelector('[data-fb-stage-name]');
      if (img instanceof HTMLImageElement) {
        this.#openLightbox(img.dataset.full || img.src, name instanceof HTMLElement ? name.textContent : '');
      }
      return;
    }
    if (target.closest('[data-fb-sheetscrim]')) {
      event.preventDefault();
      this.#closeSheets();
      return;
    }

    // Inline chooser card.
    const choose = target.closest('[data-fb-choose-print]');
    if (choose instanceof HTMLElement) {
      event.preventDefault();
      this.#setDesign(choose.dataset.key || null, {});
      return;
    }
    // "Change print" on the chip -> reopen the chooser.
    if (target.closest('[data-fb-print-change]')) {
      event.preventDefault();
      const chooser = this.querySelector('[data-fb-chooser]');
      if (chooser instanceof HTMLElement) {
        chooser.hidden = false;
        chooser.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Slot corner controls (pencil = edit, X = remove with undo).
    const slotEdit = target.closest('[data-fb-slot-edit]');
    if (slotEdit instanceof HTMLElement) {
      event.preventDefault();
      const el = slotEdit.closest('[data-fb-slot]');
      const slot = el instanceof HTMLElement ? this.#slotById(el.dataset.slotId) : null;
      if (slot && slot.entry) {
        if (slot.entry.variantId) this.#selectedVariant.set(slot.entry.productId, slot.entry.variantId);
        this.#openDrawer(slot.entry.productId, slot.id, slot.needsColor ? { preselectSize: slot.size } : {});
      }
      return;
    }
    const railBtn = target.closest('[data-fb-rail-prev], [data-fb-rail-next]');
    if (railBtn instanceof HTMLElement) {
      event.preventDefault();
      const row = this.querySelector('[data-fb-chooser-row]');
      if (row instanceof HTMLElement) {
        const dir = railBtn.hasAttribute('data-fb-rail-next') ? 1 : -1;
        row.scrollBy({ left: dir * row.clientWidth * 0.85, behavior: 'smooth' });
      }
      return;
    }
    const slotX = target.closest('[data-fb-slot-x]');
    if (slotX instanceof HTMLElement) {
      event.preventDefault();
      const el = slotX.closest('[data-fb-slot]');
      const slot = el instanceof HTMLElement ? this.#slotById(el.dataset.slotId) : null;
      if (slot) this.#removeSlot(slot);
      return;
    }
    if (target.closest('[data-fb-reset]')) {
      event.preventDefault();
      this.#resetAll();
      return;
    }

    // Slot taps.
    const slotBtn = target.closest('[data-fb-slot-btn]');
    if (slotBtn instanceof HTMLElement) {
      event.preventDefault();
      const el = slotBtn.closest('[data-fb-slot]');
      const slot = el instanceof HTMLElement ? this.#slotById(el.dataset.slotId) : null;
      if (!slot) return;
      if (!this.#design) {
        const chooser = this.querySelector('[data-fb-chooser]');
        if (chooser instanceof HTMLElement) {
          chooser.hidden = false;
          chooser.classList.add('is-pulse');
          setTimeout(() => chooser.classList.remove('is-pulse'), 900);
          chooser.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (slot.needsColor && slot.entry) {
        this.#openDrawer(slot.entry.productId, slot.id, { preselectSize: slot.size });
        return;
      }
      if (slot.entry) {
        this.#openActionSheet(slot);
        return;
      }
      this.#openGarmentSheet(slot);
      return;
    }

    // Add-another card.
    if (target.closest('[data-fb-add-btn]')) {
      event.preventDefault();
      if (this.#pieceCount() < this.#maxItems) this.#openPersonSheet();
      return;
    }

    // Garment mini-sheet pick.
    const garmentPick = target.closest('[data-fb-garment-pick]');
    if (garmentPick instanceof HTMLElement) {
      event.preventDefault();
      this.#closeSheets();
      this.#openDrawer(Number(garmentPick.dataset.id), Number(garmentPick.dataset.slot));
      return;
    }
    if (target.closest('[data-fb-garment-browse]')) {
      event.preventDefault();
      this.#closeSheets();
      const browse = this.querySelector('[data-fb-browse]');
      if (browse instanceof HTMLDetailsElement) {
        browse.open = true;
        browse.scrollIntoView({ behavior: 'smooth', block: 'start' });
        ligFbTrack('browse_open', {});
      }
      return;
    }

    // Person picker pick.
    const personPick = target.closest('[data-fb-person-pick]');
    if (personPick instanceof HTMLElement) {
      event.preventDefault();
      this.#closeSheets();
      this.#addPerson(personPick.dataset.type || 'adult', personPick.dataset.label || 'Someone');
      return;
    }
    // "That's everyone" — end the progressive prompt loop.
    if (target.closest('[data-fb-person-done]')) {
      event.preventDefault();
      this.#closeSheets();
      return;
    }

    // Rename save (inside the action sheet; do not close the sheet on input taps).
    if (target.closest('[data-fb-rename-save]')) {
      event.preventDefault();
      const sheet = this.querySelector('[data-fb-action-sheet]');
      const slot = sheet instanceof HTMLElement ? this.#slotById(sheet.dataset.slot) : null;
      const input = this.querySelector('[data-fb-rename-input]');
      if (slot && input instanceof HTMLInputElement) {
        const next = input.value.trim();
        if (next) {
          slot.label = next.slice(0, 24);
          this.#renderLineup();
          this.#renderBar();
          this.#persist();
        }
      }
      this.#closeSheets();
      return;
    }
    if (target.closest('[data-fb-rename-row]')) {
      // Taps inside the rename row (the input) should not fall through.
      return;
    }

    // Action sheet actions.
    const action = target.closest('[data-fb-action]');
    if (action instanceof HTMLElement) {
      event.preventDefault();
      const sheet = this.querySelector('[data-fb-action-sheet]');
      const slot = sheet instanceof HTMLElement ? this.#slotById(sheet.dataset.slot) : null;
      const kind = action.dataset.fbAction || action.getAttribute('data-fb-action');
      if (kind === 'rename') {
        // Reveal the inline rename row instead of closing.
        const row = this.querySelector('[data-fb-rename-row]');
        const input = this.querySelector('[data-fb-rename-input]');
        if (row instanceof HTMLElement && slot) {
          row.hidden = false;
          if (input instanceof HTMLInputElement) {
            input.value = slot.label;
            input.focus();
            input.select();
          }
        }
        return;
      }
      this.#closeSheets();
      if (!slot) return;
      if (kind === 'remove') {
        this.#removeSlot(slot);
      } else if (kind === 'garment') {
        this.#openGarmentSheet(slot);
      } else if (kind === 'edit' && slot.entry) {
        if (slot.entry.variantId) this.#selectedVariant.set(slot.entry.productId, slot.entry.variantId);
        this.#openDrawer(slot.entry.productId, slot.id);
      }
      return;
    }

    // Browse accordion row -> drawer (browse mode: fills a matching empty slot).
    const browseOpen = target.closest('[data-fb-browse-open]');
    if (browseOpen instanceof HTMLElement) {
      event.preventDefault();
      this.#openDrawer(Number(browseOpen.dataset.id), null);
      return;
    }

    // Summary bar expand / close.
    if (target.closest('[data-fb-bar-expand]')) {
      event.preventDefault();
      this.#toggleSheet();
      return;
    }
    if (target.closest('[data-fb-sheet-close]')) {
      event.preventDefault();
      this.#toggleSheet(false);
      return;
    }

    // Undo.
    if (target.closest('[data-fb-undo-btn]')) {
      event.preventDefault();
      this.#undo();
      return;
    }

    // Gallery + drawer internals.
    const colorBtn = target.closest('[data-fb-color]');
    if (colorBtn instanceof HTMLButtonElement && !colorBtn.disabled) {
      event.preventDefault();
      this.#chooseColor(colorBtn);
      return;
    }
    if (target.closest('[data-fb-gallery-prev]')) {
      event.preventDefault();
      this.#galleryStep(-1);
      return;
    }
    if (target.closest('[data-fb-gallery-next]')) {
      event.preventDefault();
      this.#galleryStep(1);
      return;
    }
    const thumb = target.closest('[data-fb-thumb]');
    if (thumb instanceof HTMLElement) {
      event.preventDefault();
      this.#galleryGoTo(Number(thumb.dataset.index) || 0);
      return;
    }
    if (target.closest('[data-fb-slide]')) {
      event.preventDefault();
      this.#toggleZoom();
      return;
    }
    const sizeBtn = target.closest('[data-fb-size]');
    if (sizeBtn instanceof HTMLButtonElement && !sizeBtn.disabled) {
      event.preventDefault();
      this.#chooseSize(sizeBtn);
      return;
    }
    if (target.closest('[data-fb-drawer-add]')) {
      event.preventDefault();
      this.#addFromDrawer();
      return;
    }
    const removeBtn = target.closest('[data-fb-remove]');
    if (removeBtn instanceof HTMLElement) {
      event.preventDefault();
      const slot = this.#slotById(removeBtn.dataset.key);
      if (slot) this.#removeSlot(slot);
      return;
    }
    if (target.closest('[data-fb-checkout]')) {
      event.preventDefault();
      this.#addSetToCart();
      return;
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const lightbox = this.querySelector('[data-fb-lightbox]');
    if (lightbox instanceof HTMLElement && !lightbox.hidden) {
      this.#closeLightbox();
      return;
    }
    const sizeGuide = this.querySelector('[data-fb-sizeguide]');
    if (sizeGuide instanceof HTMLElement && !sizeGuide.hidden) {
      this.#closeSizeGuide();
      return;
    }
    if (this.#openProductId != null) {
      this.#closeDrawer();
      return;
    }
    this.#closeSheets();
    this.#toggleSheet(false);
  };

  /* --- Drawer (battle-tested internals; slot-aware shell) --------------------- */

  #openDrawer(productId, slotId, opts = {}) {
    const product = this.#byId.get(Number(productId));
    this.elDrawer = this.querySelector('[data-fb-drawer]');
    this.elScrim = this.querySelector('[data-fb-scrim]');
    this.elDrawerTitle = this.querySelector('[data-fb-drawer-title]');
    this.elDrawerPrice = this.querySelector('[data-fb-drawer-price]');
    this.elDrawerBlurb = this.querySelector('[data-fb-drawer-blurb]');
    this.elDrawerSizes = this.querySelector('[data-fb-drawer-sizes]');
    this.elDrawerAdd = this.querySelector('[data-fb-drawer-add]');
    if (!product || !this.elDrawer) return;
    this.#openProductId = Number(productId);
    this.#drawerSlotId = slotId != null ? Number(slotId) : null;
    this.#zoomed = false;
    this.#pinch.scale = 1;

    if (opts.preselectSize) {
      const idx = this.#sizeOptionIndex(product);
      const v = this.#resolveBySize(product, idx, opts.preselectSize);
      if (v) this.#selectedVariant.set(Number(product.id), Number(v.id));
    }

    const kicker = this.querySelector('[data-fb-drawer-kicker]');
    const slot = this.#drawerSlotId != null ? this.#slotById(this.#drawerSlotId) : null;
    if (kicker instanceof HTMLElement) {
      kicker.textContent = slot ? `Dressing ${slot.label}` : '';
      kicker.hidden = !slot;
    }

    if (this.elDrawerTitle) this.elDrawerTitle.textContent = product.title || '';
    if (this.elDrawerBlurb) {
      const blurb = product.blurb || '';
      this.elDrawerBlurb.textContent = blurb;
      this.elDrawerBlurb.hidden = !blurb;
    }

    this.#syncDrawerMeta(product);
    this.#renderColors(product);
    this.#renderGallery(product);
    this.#renderSizes(product);
    this.#syncDrawerPrice(product);
    this.#syncDrawerAdd(product);

    this.elDrawer.classList.add('is-open');
    if (this.elScrim instanceof HTMLElement) this.elScrim.classList.add('is-open');
    this.elDrawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lig-fb-locked');
    const focusTarget = this.querySelector('[data-fb-drawer-close]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  #closeDrawer() {
    if (!this.elDrawer) return;
    this.#closeSizeGuide();
    this.elDrawer.classList.remove('is-open');
    if (this.elScrim instanceof HTMLElement) this.elScrim.classList.remove('is-open');
    this.elDrawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lig-fb-locked');
    this.#zoomed = false;
    const slotId = this.#drawerSlotId;
    this.#openProductId = null;
    this.#drawerSlotId = null;
    if (slotId != null) {
      const el = this.querySelector(`[data-fb-slot][data-slot-id="${slotId}"] [data-fb-slot-btn]`);
      if (el instanceof HTMLElement) el.focus();
    }
  }

  /** Drawer CTA -> fill the slot (or browse-mode: matching empty slot / new instance). */
  #addFromDrawer() {
    if (this.#openProductId == null) return;
    const product = this.#byId.get(this.#openProductId);
    if (!product) return;
    const variant = this.#currentVariant(product);
    if (!variant || variant.available === false) return;

    let slot = this.#drawerSlotId != null ? this.#slotById(this.#drawerSlotId) : null;
    if (!slot) {
      // Browse mode: fill the first empty slot whose menu contains this garment,
      // else append a generic instance.
      const type = String(product.type || '');
      slot =
        this.#slots.find(
          (s) => !s.entry && (LIG_FB_SLOT_GARMENTS[s.type] || []).some((g) => g.toLowerCase() === type.toLowerCase())
        ) || null;
      if (!slot) {
        if (this.#pieceCount() >= this.#maxItems) return;
        this.#slotSeq += 1;
        slot = {
          id: this.#slotSeq,
          label: LIG_BB_CAT_LABEL[ligBbCategory(type)] || 'Someone',
          type: ligBbCategory(type) === 'kids' ? 'kid' : ligBbCategory(type),
          base: false,
          entry: null,
          needsColor: false,
          size: null,
        };
        this.#slots.push(slot);
      }
    }
    const wasEmpty = !slot.entry;
    if (wasEmpty && this.#pieceCount() >= this.#maxItems) return;

    slot.entry = { productId: Number(product.id), variantId: Number(variant.id) };
    slot.needsColor = false;
    slot.size = null;
    this.#closeDrawer();
    this.#renderLineup();
    this.#renderBar();
    this.#persist();
    ligFbTrack('slot_filled', { type: slot.type, garment: product.type });
    // Progressive flow: after a just-added person gets their piece, ask
    // "Who is next?" once (with a "That's everyone" exit).
    if (slot.fresh) {
      slot.fresh = false;
      if (this.#pieceCount() < this.#maxItems) {
        setTimeout(() => this.#openPersonSheet(), 250);
      }
    }
  }

  /* --- Gallery / colors / sizes / fit (ported verbatim) ----------------------- */

  #norm(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  #colorOptionIndex(product) {
    const options = Array.isArray(product?.options) ? product.options : [];
    return options.findIndex((o) => o && /colou?r/i.test(String(o.name)));
  }

  #selectedColor(product) {
    const ci = this.#colorOptionIndex(product);
    if (ci < 0) return null;
    const v = this.#currentVariant(product);
    return v ? v[`option${ci + 1}`] : null;
  }

  #imageMeta(alt) {
    const text = String(alt || '');
    const front = /\(front\)/i.test(text);
    const lifestyle = /lifestyle/i.test(text) || text.trim() === '';
    let color = null;
    const parts = text.split('—');
    if (parts.length > 1) {
      color = parts[1].replace(/\((?:front|back)\)/i, '').trim();
      if (/lifestyle/i.test(color) || !color) color = null;
    }
    return { color, front, lifestyle };
  }

  #gallerySlidesFor(product) {
    const imgs =
      Array.isArray(product.images) && product.images.length
        ? product.images
        : product.image
          ? [{ url: product.image, alt: product.title }]
          : [];
    const sel = this.#norm(this.#selectedColor(product));
    const meta = imgs.map((im) => ({ url: im.url, alt: im.alt, ...this.#imageMeta(im.alt) }));
    let colorMatched = sel ? meta.filter((m) => m.color && this.#norm(m.color) === sel) : [];
    const lifestyle = meta.filter((m) => m.lifestyle || !m.color);
    if (colorMatched.length === 0) {
      colorMatched = meta.filter((m) => m.color);
      if (colorMatched.length === 0) colorMatched = meta.filter((m) => !m.lifestyle);
    }
    colorMatched.sort((a, b) => (b.front ? 1 : 0) - (a.front ? 1 : 0));
    const ordered = [...colorMatched, ...lifestyle];
    const seen = new Set();
    const out = [];
    for (const m of ordered) {
      if (seen.has(m.url)) continue;
      seen.add(m.url);
      out.push(m);
    }
    return out.length ? out : meta;
  }

  #renderGallery(product) {
    const track = this.querySelector('[data-fb-gallery-track]');
    const thumbs = this.querySelector('[data-fb-thumbs]');
    if (!(track instanceof HTMLElement)) return;

    const slides = this.#gallerySlidesFor(product);
    this.#gallerySlides = slides;
    this.#galleryIndex = 0;
    this.#zoomed = false;
    const gallery = this.querySelector('[data-fb-gallery]');
    if (gallery) gallery.classList.remove('is-zoomed');

    const tf = document.createDocumentFragment();
    slides.forEach((s, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lig-fb__slide';
      btn.setAttribute('data-fb-slide', '');
      btn.dataset.index = String(i);
      const img = document.createElement('img');
      img.className = 'lig-fb__slide-img';
      img.src = this.#withWidth(s.url, 1100);
      img.alt = s.alt || product.title || '';
      img.loading = i === 0 ? 'eager' : 'lazy';
      img.draggable = false;
      btn.appendChild(img);
      tf.appendChild(btn);
    });
    track.replaceChildren(tf);
    track.scrollTo({ left: 0, behavior: 'instant' });

    if (thumbs instanceof HTMLElement) {
      if (slides.length > 1) {
        thumbs.hidden = false;
        const thf = document.createDocumentFragment();
        slides.forEach((s, i) => {
          const t = document.createElement('button');
          t.type = 'button';
          t.className = 'lig-fb__thumb' + (i === 0 ? ' is-active' : '');
          t.setAttribute('data-fb-thumb', '');
          t.dataset.index = String(i);
          const ti = document.createElement('img');
          ti.src = this.#withWidth(s.url, 200);
          ti.alt = '';
          ti.loading = 'lazy';
          t.appendChild(ti);
          thf.appendChild(t);
        });
        thumbs.replaceChildren(thf);
      } else {
        thumbs.hidden = true;
        thumbs.replaceChildren();
      }
    }
    this.#updateGalleryUi();
  }

  #updateGalleryUi() {
    const n = this.#gallerySlides.length;
    const i = this.#galleryIndex;
    const counter = this.querySelector('[data-fb-gallery-counter]');
    if (counter instanceof HTMLElement) {
      counter.textContent = n > 1 ? `${i + 1} / ${n}` : '';
      counter.hidden = n <= 1;
    }
    const prev = this.querySelector('[data-fb-gallery-prev]');
    const next = this.querySelector('[data-fb-gallery-next]');
    if (prev instanceof HTMLElement) prev.hidden = n <= 1;
    if (next instanceof HTMLElement) next.hidden = n <= 1;
    this.querySelectorAll('[data-fb-thumb]').forEach((t) => {
      t.classList.toggle('is-active', Number(t.dataset.index) === i);
    });
  }

  #onGalleryScroll = () => {
    if (this.#galleryAnimating) return;
    const track = this.elGalleryTrack;
    if (!(track instanceof HTMLElement)) return;
    const w = track.clientWidth || 1;
    const i = Math.max(0, Math.min(Math.round(track.scrollLeft / w), this.#gallerySlides.length - 1));
    if (i !== this.#galleryIndex) {
      this.#galleryIndex = i;
      if (this.#zoomed) {
        this.#zoomed = false;
        const g = this.querySelector('[data-fb-gallery]');
        if (g) g.classList.remove('is-zoomed');
      }
      this.#updateGalleryUi();
    }
  };

  #galleryStep(dir) {
    const target = Math.max(0, Math.min(this.#galleryIndex + dir, this.#gallerySlides.length - 1));
    this.#galleryScrollTo(target);
  }

  #galleryGoTo(i) {
    const target = Math.max(0, Math.min(i, this.#gallerySlides.length - 1));
    this.#galleryScrollTo(target);
  }

  /**
   * Native scrollTo({behavior:'smooth'}) is a silent no-op on this snap
   * container, so we animate scrollLeft by hand with rAF (snap disabled
   * during the run). Do not "simplify" this.
   */
  #galleryScrollTo(target) {
    const track = this.elGalleryTrack;
    if (!(track instanceof HTMLElement)) return;
    this.#galleryIndex = target;
    this.#updateGalleryUi();
    const to = target * track.clientWidth;
    const from = track.scrollLeft;
    cancelAnimationFrame(this.#galleryRaf);
    if (Math.abs(to - from) < 2) return;
    this.#galleryAnimating = true;
    track.style.scrollSnapType = 'none';
    const start = performance.now();
    const dur = 300;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      track.scrollLeft = from + (to - from) * ease(t);
      if (t < 1) {
        this.#galleryRaf = requestAnimationFrame(step);
      } else {
        track.style.scrollSnapType = '';
        this.#galleryAnimating = false;
      }
    };
    this.#galleryRaf = requestAnimationFrame(step);
  }

  #toggleZoom = () => {
    const gallery = this.querySelector('[data-fb-gallery]');
    if (!(gallery instanceof HTMLElement)) return;
    this.#zoomed = !this.#zoomed;
    gallery.classList.toggle('is-zoomed', this.#zoomed);
    const slide = this.#gallerySlides[this.#galleryIndex];
    const imgs = this.querySelectorAll('[data-fb-slide] img');
    const active = imgs[this.#galleryIndex];
    if (active instanceof HTMLImageElement && slide) {
      active.src = this.#withWidth(slide.url, this.#zoomed ? 2000 : 1100);
    }
  };

  #renderColors(product) {
    const host = this.querySelector('[data-fb-drawer-colors]');
    const head = this.querySelector('[data-fb-color-head]');
    const valueEl = head ? head.querySelector('[data-fb-color-value]') : null;
    if (!(host instanceof HTMLElement)) return;
    const ci = this.#colorOptionIndex(product);
    if (ci < 0) {
      host.hidden = true;
      host.replaceChildren();
      if (head instanceof HTMLElement) head.hidden = true;
      return;
    }
    const option = product.options[ci];
    const values = Array.isArray(option?.values) ? option.values : [];
    const key = `option${ci + 1}`;
    const current = this.#currentVariant(product);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (head instanceof HTMLElement) head.hidden = false;
    if (valueEl) valueEl.textContent = current ? String(current[key] || '') : String(values[0] || '');

    if (values.length <= 1) {
      host.hidden = true;
      host.replaceChildren();
      return;
    }
    host.hidden = false;
    const frag = document.createDocumentFragment();
    for (const val of values) {
      const available = variants.some((v) => String(v[key]) === String(val) && v.available);
      const selected = current && String(current[key]) === String(val);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lig-fb__swatch' + (selected ? ' is-selected' : '') + (available ? '' : ' is-unavailable');
      b.setAttribute('data-fb-color', '');
      b.dataset.optionIndex = String(ci);
      b.dataset.value = String(val);
      b.title = String(val);
      b.setAttribute('aria-label', String(val));
      b.setAttribute('aria-pressed', selected ? 'true' : 'false');
      b.style.setProperty('--lig-fb-swatch', this.#swatchMap.get(this.#norm(val)) || this.#colorMap[this.#norm(val)] || '#c9c9c9');
      if (!available) {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
      }
      frag.appendChild(b);
    }
    host.replaceChildren(frag);
  }

  #chooseColor(button) {
    if (this.#openProductId == null) return;
    const product = this.#byId.get(this.#openProductId);
    if (!product) return;
    const optionIndex = Number(button.dataset.optionIndex) || 0;
    const value = button.dataset.value || '';
    const variant = this.#resolveBySize(product, optionIndex, value);
    if (!variant) return;
    this.#selectedVariant.set(Number(product.id), Number(variant.id));
    this.#renderColors(product);
    this.#renderSizes(product);
    this.#syncDrawerPrice(product);
    this.#syncDrawerAdd(product);
    this.#renderGallery(product);
  }

  #syncDrawerMeta(product) {
    const cat = ligBbCategory(product.type);
    const data = LIG_BB_SIZE[ligBbChartKey(product.type)] || LIG_BB_SIZE.adult;
    const badge = this.querySelector('[data-fb-drawer-cat]');
    if (badge instanceof HTMLElement) {
      badge.textContent = LIG_BB_CAT_LABEL[cat];
      badge.className = 'lig-fb__cat lig-fb__drawer-cat lig-fb__cat--' + cat;
      badge.hidden = false;
    }
    const tag = this.querySelector('[data-fb-fit-tag]');
    if (tag instanceof HTMLElement) tag.textContent = data.fit.tag;
    const marker = this.querySelector('[data-fb-fit-marker]');
    if (marker instanceof HTMLElement) marker.style.insetInlineStart = data.fit.pos + '%';
    const note = this.querySelector('[data-fb-fit-note]');
    if (note instanceof HTMLElement) note.textContent = data.fit.note;
  }

  #openSizeGuide() {
    if (this.#openProductId == null) return;
    const product = this.#byId.get(this.#openProductId);
    if (!product) return;
    const data = LIG_BB_SIZE[ligBbChartKey(product.type)] || LIG_BB_SIZE.adult;
    const modal = this.querySelector('[data-fb-sizeguide]');
    if (!(modal instanceof HTMLElement)) return;

    const title = this.querySelector('[data-fb-sizeguide-title]');
    const sub = this.querySelector('[data-fb-sizeguide-sub]');
    const body = this.querySelector('[data-fb-sizeguide-body]');
    if (title instanceof HTMLElement) title.textContent = data.label + ' — size guide';
    if (sub instanceof HTMLElement) sub.textContent = `${data.blank} · ${data.fit.tag}. ${data.fit.note}`;
    if (body instanceof HTMLElement) {
      if (Array.isArray(data.rows) && data.rows.length) {
        const table = document.createElement('table');
        table.className = 'lig-fb__sizetable';
        const thead = document.createElement('thead');
        const htr = document.createElement('tr');
        for (const c of ['Size', 'Chest', 'Length']) {
          const th = document.createElement('th');
          th.textContent = c;
          htr.appendChild(th);
        }
        thead.appendChild(htr);
        table.appendChild(thead);
        const tbody = document.createElement('tbody');
        for (const row of data.rows) {
          const tr = document.createElement('tr');
          row.forEach((cell, i) => {
            const td = document.createElement(i === 0 ? 'th' : 'td');
            td.textContent = cell + (i === 0 ? '' : '"');
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        }
        table.appendChild(tbody);
        body.replaceChildren(table);
      } else {
        const p = document.createElement('p');
        p.className = 'lig-fb__sizenote';
        p.textContent = `Sizes run ${data.fit.tag.toLowerCase()}. ${data.fit.note}`;
        body.replaceChildren(p);
      }
    }
    modal.hidden = false;
    requestAnimationFrame(() => modal.classList.add('is-open'));
  }

  #closeSizeGuide() {
    const modal = this.querySelector('[data-fb-sizeguide]');
    if (!(modal instanceof HTMLElement)) return;
    modal.classList.remove('is-open');
    modal.hidden = true;
  }

  #renderSizes(product) {
    if (!(this.elDrawerSizes instanceof HTMLElement)) return;
    const options = Array.isArray(product.options) ? product.options : [];
    const variants = Array.isArray(product.variants) ? product.variants : [];

    if (!options.length || !variants.length) {
      this.elDrawerSizes.replaceChildren();
      this.elDrawerSizes.hidden = true;
      return;
    }
    this.elDrawerSizes.hidden = false;

    const sizeIdx = this.#sizeOptionIndex(product);
    const sizeOption = options[sizeIdx];
    const values = Array.isArray(sizeOption?.values) ? sizeOption.values : [];
    const key = `option${sizeIdx + 1}`;
    const current = this.#currentVariant(product);

    const fragment = document.createDocumentFragment();
    for (const value of values) {
      const available = variants.some((v) => String(v[key]) === String(value) && v.available);
      const isSelected = current && String(current[key]) === String(value);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lig-fb__size';
      btn.setAttribute('data-fb-size', '');
      btn.dataset.optionIndex = String(sizeIdx);
      btn.dataset.value = String(value);
      btn.textContent = String(value);
      if (!available) {
        btn.disabled = true;
        btn.classList.add('is-unavailable');
        btn.setAttribute('aria-disabled', 'true');
        btn.title = 'Sold out';
      }
      if (isSelected && available) {
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.setAttribute('aria-pressed', 'false');
      }
      fragment.appendChild(btn);
    }
    this.elDrawerSizes.replaceChildren(fragment);
  }

  #chooseSize(button) {
    if (this.#openProductId == null) return;
    const product = this.#byId.get(this.#openProductId);
    if (!product) return;
    const optionIndex = Number(button.dataset.optionIndex) || 0;
    const value = button.dataset.value || '';
    const variant = this.#resolveBySize(product, optionIndex, value);
    if (!variant) return;
    this.#selectedVariant.set(Number(product.id), Number(variant.id));
    this.#renderSizes(product);
    this.#syncDrawerPrice(product);
    this.#syncDrawerAdd(product);
  }

  #syncDrawerPrice(product) {
    if (!(this.elDrawerPrice instanceof HTMLElement)) return;
    const variant = this.#currentVariant(product);
    const cents = variant ? variant.price : product.price;
    this.elDrawerPrice.textContent = this.#money(cents);
  }

  #syncDrawerAdd(product) {
    if (!(this.elDrawerAdd instanceof HTMLButtonElement)) return;
    const variant = this.#currentVariant(product);
    const label = this.elDrawerAdd.querySelector('[data-fb-drawer-add-label]') || this.elDrawerAdd;
    if (!variant || variant.available === false) {
      this.elDrawerAdd.disabled = true;
      label.textContent = 'Sold out';
      return;
    }
    const slot = this.#drawerSlotId != null ? this.#slotById(this.#drawerSlotId) : null;
    const fillingEmpty = !slot || !slot.entry;
    if (fillingEmpty && this.#pieceCount() >= this.#maxItems) {
      this.elDrawerAdd.disabled = true;
      label.textContent = `Set is full (${this.#maxItems} max)`;
      return;
    }
    this.elDrawerAdd.disabled = false;
    label.textContent = slot ? (slot.entry && !slot.needsColor ? `Update ${slot.label}'s piece` : `Add to the set`) : 'Add to the set';
  }

  /* --- Variant resolution (ported verbatim) ------------------------------- */

  #defaultVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;
    return variants.find((v) => v && v.available) || variants[0];
  }

  #currentVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;
    const chosenId = this.#selectedVariant.get(Number(product.id));
    return variants.find((v) => Number(v.id) === Number(chosenId)) || this.#defaultVariant(product);
  }

  #resolveBySize(product, optionIndex, value) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;
    const key = `option${optionIndex + 1}`;
    const current = this.#currentVariant(product);
    const match = (v, exact) => {
      if (String(v[key]) !== String(value)) return false;
      for (let i = 1; i <= 3; i += 1) {
        if (i - 1 === optionIndex) continue;
        const k = `option${i}`;
        if (current && current[k] != null && exact && String(v[k]) !== String(current[k])) {
          return false;
        }
      }
      return true;
    };
    return (
      variants.find((v) => match(v, true) && v.available) ||
      variants.find((v) => match(v, true)) ||
      variants.find((v) => String(v[key]) === String(value) && v.available) ||
      variants.find((v) => String(v[key]) === String(value)) ||
      null
    );
  }

  #sizeOptionIndex(product) {
    const options = Array.isArray(product?.options) ? product.options : [];
    if (!options.length) return 0;
    const idx = options.findIndex((o) => o && /size/i.test(String(o.name)));
    return idx >= 0 ? idx : options.length - 1;
  }

  /* --- Tiers + summary bar --------------------------------------------------- */

  #activeTier() {
    const count = this.#filledCount();
    let active = null;
    for (const tier of this.#tiers) {
      if (tier.min <= count) active = tier;
    }
    return active;
  }

  #nextTier() {
    const count = this.#filledCount();
    for (const tier of this.#tiers) {
      if (tier.min > count) return tier;
    }
    return null;
  }

  #toggleSheet(force) {
    const sheet = this.querySelector('[data-fb-sheet]');
    const expand = this.querySelector('[data-fb-bar-expand]');
    if (!(sheet instanceof HTMLElement)) return;
    const open = typeof force === 'boolean' ? force : sheet.hidden;
    sheet.hidden = !open;
    if (expand instanceof HTMLElement) expand.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) this.#renderSheet();
  }

  #renderBar() {
    const bar = this.querySelector('[data-fb-bar]');
    if (!(bar instanceof HTMLElement)) return;
    const resolved = this.#slots.filter((s) => s.entry && s.entry.variantId);
    const pending = this.#slots.filter((s) => s.entry && !s.entry.variantId).length;
    if (!resolved.length && !pending) {
      bar.hidden = true;
      this.#toggleSheet(false);
      this.dataset.fbCount = '0';
      return;
    }
    bar.hidden = false;

    let subtotal = 0;
    for (const s of resolved) {
      const product = this.#byId.get(s.entry.productId);
      const variant = product ? (product.variants || []).find((v) => Number(v.id) === s.entry.variantId) : null;
      subtotal += Number(variant ? variant.price : product ? product.price : 0) || 0;
    }
    const tier = this.#activeTier();
    const pct = tier ? tier.pct : 0;
    const savings = Math.round((subtotal * pct) / 100);
    const total = subtotal - savings;

    const count = this.querySelector('[data-fb-bar-count]');
    if (count instanceof HTMLElement) {
      count.textContent = `${resolved.length} dressed${pending ? ` · ${pending} need${pending === 1 ? 's' : ''} a color` : ''}`;
    }
    const totalEl = this.querySelector('[data-fb-bar-total]');
    if (totalEl instanceof HTMLElement) totalEl.textContent = this.#money(total);

    const checkout = this.querySelector('[data-fb-checkout]');
    if (checkout instanceof HTMLButtonElement) {
      checkout.disabled = resolved.length === 0 || checkout.getAttribute('aria-busy') === 'true';
      const label = checkout.querySelector('[data-fb-checkout-label]') || checkout;
      if (checkout.getAttribute('aria-busy') !== 'true') {
        label.textContent = resolved.length ? `Add ${resolved.length} to cart` : 'Add set to cart';
      }
    }
    this.dataset.fbCount = String(resolved.length);
    if (!this.querySelector('[data-fb-sheet]')?.hidden) this.#renderSheet();
  }

  #renderSheet() {
    const list = this.querySelector('[data-fb-set]');
    if (!(list instanceof HTMLElement)) return;
    const frag = document.createDocumentFragment();
    let subtotal = 0;
    for (const slot of this.#slots) {
      if (!slot.entry) continue;
      const product = this.#byId.get(slot.entry.productId);
      if (!product) continue;
      const variant = slot.entry.variantId
        ? (product.variants || []).find((v) => Number(v.id) === slot.entry.variantId)
        : null;
      if (variant) subtotal += Number(variant.price) || 0;

      const li = document.createElement('li');
      li.className = 'lig-fb__line' + (slot.needsColor ? ' is-needs-color' : '');
      if (product.image) {
        const media = document.createElement('span');
        media.className = 'lig-fb__line-media';
        const img = document.createElement('img');
        img.className = 'lig-fb__line-img';
        img.src = this.#withWidth(product.image, 160);
        img.alt = '';
        img.width = 56;
        img.height = 56;
        img.loading = 'lazy';
        media.appendChild(img);
        li.appendChild(media);
      }
      const info = document.createElement('div');
      info.className = 'lig-fb__line-info';
      const name = document.createElement('span');
      name.className = 'lig-fb__line-title';
      name.textContent = slot.label;
      info.appendChild(name);
      const sub = document.createElement('span');
      sub.className = 'lig-fb__line-variant';
      const bits = [ligFbGarmentLabel(product.type)];
      if (slot.needsColor) bits.push('needs a color');
      else if (variant) {
        const label = this.#variantLabel(product, variant);
        if (label) bits.push(label);
      }
      sub.textContent = bits.join(' · ');
      info.appendChild(sub);
      li.appendChild(info);
      const price = document.createElement('span');
      price.className = 'lig-fb__line-price';
      price.textContent = variant ? this.#money(variant.price) : '';
      li.appendChild(price);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lig-fb__line-remove';
      remove.setAttribute('data-fb-remove', '');
      remove.dataset.key = String(slot.id);
      remove.setAttribute('aria-label', `Remove ${slot.label} from set`);
      remove.textContent = '×';
      li.appendChild(remove);
      frag.appendChild(li);
    }
    list.replaceChildren(frag);

    const tier = this.#activeTier();
    const pct = tier ? tier.pct : 0;
    const savings = Math.round((subtotal * pct) / 100);
    const total = subtotal - savings;

    const subRow = this.querySelector('[data-fb-subtotal-row]');
    const subEl = this.querySelector('[data-fb-subtotal]');
    const saveRow = this.querySelector('[data-fb-savings-row]');
    const saveEl = this.querySelector('[data-fb-savings]');
    const totalEl = this.querySelector('[data-fb-total]');
    const hint = this.querySelector('[data-fb-hint]');
    if (subRow instanceof HTMLElement) subRow.hidden = pct === 0;
    if (subEl instanceof HTMLElement) subEl.textContent = this.#money(subtotal);
    if (saveRow instanceof HTMLElement) saveRow.hidden = pct === 0;
    if (saveEl instanceof HTMLElement) saveEl.textContent = pct > 0 ? `You're saving ${this.#money(savings)} (${pct}%)` : '';
    if (totalEl instanceof HTMLElement) totalEl.textContent = this.#money(total);
    if (hint instanceof HTMLElement) {
      if (!this.#discounts || !this.#tiers.length) {
        hint.hidden = true;
        hint.textContent = '';
      } else {
        const next = this.#nextTier();
        if (next) {
          const needed = next.min - this.#filledCount();
          hint.hidden = false;
          hint.textContent = `Dress ${needed} more of the pack and the whole set drops to ${next.pct}% off.`;
        } else if (tier) {
          hint.hidden = false;
          hint.textContent = `Top tier unlocked. ${tier.pct}% off your set.`;
        } else {
          hint.hidden = true;
          hint.textContent = '';
        }
      }
    }
  }

  /* --- Add set to cart (proven Horizon pipeline + _member properties) -------- */

  async #addSetToCart() {
    const checkout = this.querySelector('[data-fb-checkout]');
    if (!(checkout instanceof HTMLButtonElement)) return;
    const resolved = this.#slots.filter((s) => s.entry && s.entry.variantId);
    if (!resolved.length) return;
    if (checkout.getAttribute('aria-busy') === 'true') return;

    checkout.setAttribute('aria-busy', 'true');
    checkout.disabled = true;

    const items = resolved.map((s) => ({
      id: s.entry.variantId,
      quantity: 1,
      properties: { _member: s.label },
    }));

    const sections = this.#cartSectionIds();
    const body = JSON.stringify({ items, sections, sections_url: window.location.pathname });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        throw new Error('Unexpected cart response');
      }
      if (!response.ok || parsed.status) {
        document.dispatchEvent(new CartErrorEvent('lig-family-builder', parsed.message, parsed.description, parsed.errors));
        this.#resetCheckout();
        return;
      }
      const cart = await this.#fetchCart();
      const itemCount =
        typeof parsed.item_count === 'number'
          ? parsed.item_count
          : typeof cart?.item_count === 'number'
            ? cart.item_count
            : undefined;
      document.dispatchEvent(
        new CartAddEvent(cart ?? parsed, 'lig-family-builder', {
          source: 'lig-family-builder',
          itemCount,
          setItemCount: items.length,
          sections: parsed.sections,
        })
      );
      this.#openCartDrawer();
      ligFbTrack('set_added', { pieces: items.length });

      // Reset: base slots empty out, added slots go away.
      for (const slot of this.#slots) {
        slot.entry = null;
        slot.needsColor = false;
        slot.size = null;
      }
      this.#slots = [];
      try {
        localStorage.removeItem(LIG_FB_STORAGE_KEY);
      } catch { /* no-op */ }
      this.#toggleSheet(false);
      this.#renderLineup();
      this.#renderBar();
      this.#resetCheckout();
    } catch {
      document.dispatchEvent(new CartErrorEvent('lig-family-builder', 'Could not add set to cart', undefined, undefined));
      this.#resetCheckout();
    }
  }

  #resetCheckout() {
    const checkout = this.querySelector('[data-fb-checkout]');
    if (!(checkout instanceof HTMLButtonElement)) return;
    checkout.removeAttribute('aria-busy');
    this.#renderBar();
  }

  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
      } catch { /* no-op */ }
    }
  }

  #cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) {
        ids.add(node.dataset.sectionId);
      }
    });
    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);
    return Array.from(ids).join(',');
  }

  async #fetchCart() {
    try {
      const cartUrl =
        typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url
          ? `${Theme.routes.cart_url}.js`
          : '/cart.js';
      const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  }

  /* --- Utilities --------------------------------------------------------- */

  #variantLabel(product, variant) {
    if (!variant) return '';
    const options = Array.isArray(product.options) ? product.options : [];
    if (!options.length) return '';
    const parts = [];
    for (let i = 0; i < Math.min(3, options.length); i += 1) {
      const v = variant[`option${i + 1}`];
      if (v != null && String(v) !== 'Default Title') parts.push(String(v));
    }
    return parts.join(' / ');
  }

  #withWidth(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  /** Detail imagery (macros): width + format=pjpg. The source macros are
   *  multi-MB PNGs; Shopify's CDN re-encodes to progressive JPEG, which is
   *  what makes the stage + lightbox load fast. */
  #withDetail(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      url.searchParams.set('format', 'pjpg');
      return url.toString();
    } catch {
      return src;
    }
  }

  #moneyFormatter() {
    const currency =
      (typeof window.Shopify !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    let intl;
    try {
      intl = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency });
    } catch {
      intl = new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' });
    }
    return (cents) => {
      const value = Number(cents);
      if (!Number.isFinite(value)) return '';
      return intl.format(Math.round(value) / 100);
    };
  }
}

if (!customElements.get('lig-family-builder')) {
  customElements.define('lig-family-builder', LigFamilyBuilder);
}
